/**
 * @file volAccess.js
 * @description 漫画卷 accessStatus 四态判断（禁止写死 num === 1）
 * @module features/comic/utils/volAccess
 */

/** @typedef {'FREE' | 'NEED_LOGIN' | 'LOCKED' | 'UNLOCKED'} VolAccessStatus */

export const VOL_ACCESS_STATUS = {
  FREE: 'FREE',
  NEED_LOGIN: 'NEED_LOGIN',
  LOCKED: 'LOCKED',
  UNLOCKED: 'UNLOCKED',
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
function isKnownAccessStatus(raw) {
  return (
    raw === VOL_ACCESS_STATUS.FREE ||
    raw === VOL_ACCESS_STATUS.NEED_LOGIN ||
    raw === VOL_ACCESS_STATUS.LOCKED ||
    raw === VOL_ACCESS_STATUS.UNLOCKED
  )
}

/**
 * 该漫画第一卷号（最小 num），不要写死 1
 * @param {Array<{ num?: number }>|null|undefined} volInfoVos
 * @returns {number}
 */
export function getFirstFreeVolNum(volInfoVos) {
  if (!Array.isArray(volInfoVos) || !volInfoVos.length) return 1
  const nums = volInfoVos
    .map((vol) => Number(vol?.num))
    .filter((n) => Number.isFinite(n))
  return nums.length ? Math.min(...nums) : 1
}

/**
 * @param {Array<{ num?: number, id?: string }>|null|undefined} volInfoVos
 * @param {number|string} volNum
 * @returns {object|null}
 */
export function findVolByNum(volInfoVos, volNum) {
  if (!Array.isArray(volInfoVos) || !volInfoVos.length) return null
  const target = Number(volNum)
  return volInfoVos.find((vol) => Number(vol?.num) === target) || null
}

/**
 * 解析卷访问态；无 accessStatus 时按最小卷号 + 登录态兜底
 * @param {{ accessStatus?: string, num?: number, chargeFlag?: number }|null|undefined} vol
 * @param {{ hasToken?: boolean, firstVolNum?: number }} [options]
 * @returns {VolAccessStatus}
 */
export function resolveVolAccessStatus(vol, options = {}) {
  if (isKnownAccessStatus(vol?.accessStatus)) {
    return /** @type {VolAccessStatus} */ (vol.accessStatus)
  }
  const hasToken = Boolean(options.hasToken)
  const firstVolNum = Number(options.firstVolNum)
  const volNum = Number(vol?.num)
  const isFirst =
    Number.isFinite(volNum) && Number.isFinite(firstVolNum)
      ? volNum === firstVolNum
      : Number.isFinite(volNum) && volNum === 1
  if (isFirst) return VOL_ACCESS_STATUS.FREE
  if (!hasToken) return VOL_ACCESS_STATUS.NEED_LOGIN
  if (Number(vol?.chargeFlag) === 1) return VOL_ACCESS_STATUS.LOCKED
  return VOL_ACCESS_STATUS.UNLOCKED
}

/**
 * @param {VolAccessStatus|string} status
 * @returns {boolean}
 */
export function canEnterReader(status) {
  return (
    status === VOL_ACCESS_STATUS.FREE ||
    status === VOL_ACCESS_STATUS.LOCKED ||
    status === VOL_ACCESS_STATUS.UNLOCKED
  )
}

/**
 * @param {VolAccessStatus|string} status
 * @returns {boolean}
 */
export function isNeedLoginAccess(status) {
  return status === VOL_ACCESS_STATUS.NEED_LOGIN
}

/**
 * 未登录且目标卷为 NEED_LOGIN 时拦截
 * @param {Array<object>|null|undefined} volInfoVos
 * @param {number|string} volNum
 * @param {boolean} hasToken
 * @returns {boolean}
 */
export function shouldPromptLoginForVol(volInfoVos, volNum, hasToken) {
  if (hasToken) return false
  const firstVolNum = getFirstFreeVolNum(volInfoVos)
  const vol = findVolByNum(volInfoVos, volNum)
  if (vol) {
    return resolveVolAccessStatus(vol, { hasToken: false, firstVolNum }) ===
      VOL_ACCESS_STATUS.NEED_LOGIN
  }
  return Number(volNum) !== firstVolNum
}

/**
 * 购买成功后把对应卷标成已解锁
 * @param {Array<{ id?: string, accessStatus?: string }>|null|undefined} volInfoVos
 * @param {string[]} unlockedVolIds
 */
export function markVolsUnlocked(volInfoVos, unlockedVolIds) {
  if (!Array.isArray(volInfoVos) || !unlockedVolIds?.length) return
  const idSet = new Set(unlockedVolIds.map((id) => String(id)))
  volInfoVos.forEach((vol) => {
    if (vol?.id != null && idSet.has(String(vol.id))) {
      vol.accessStatus = VOL_ACCESS_STATUS.UNLOCKED
    }
  })
}
